import { create } from 'zustand';
import { useTransactionStore as useCoreTransactionStore } from '@flows.cash/core';
import { toast } from 'sonner';
import { queryClient } from '@/lib/queryClient';
import { 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear,
  isWithinInterval,
  parseISO
} from 'date-fns';
import { useAuthStore } from './authStore';
import { Transaction, Category} from '@flows.cash/types';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/lib/supabase';

// Debug utility - only logs in development
const debugLog = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[TransactionsStore] ${message}`, data);
  }
};

type TransactionStoreFilters = {
  dateRange: string;
  type: string;
  category: string;
  accounts: string[];
  search: string;
  payee: string;
  tags: string[];
  customDateRange?: {
    from: string;
    to: string;
  };
};

const defaultFilters: TransactionStoreFilters = {
  dateRange: 'all',
  type: 'all',
  category: 'all',
  accounts: [],
  search: '',
  payee: 'all',
  tags: []
};

// Load saved filters from localStorage
const loadSavedFilters = (): TransactionStoreFilters => {
  try {
    const saved = localStorage.getItem('transaction-filters');
    if (saved) {
      const parsedFilters = JSON.parse(saved);
      debugLog('Loaded saved filters from localStorage:', parsedFilters);
      
      // Ensure the loaded filters have all required properties with proper defaults
      const loadedFilters: TransactionStoreFilters = {
        dateRange: parsedFilters.dateRange || defaultFilters.dateRange,
        type: parsedFilters.type || defaultFilters.type,
        category: parsedFilters.category || defaultFilters.category,
        accounts: Array.isArray(parsedFilters.accounts) ? parsedFilters.accounts : defaultFilters.accounts,
        search: parsedFilters.search || defaultFilters.search,
        payee: parsedFilters.payee || defaultFilters.payee,
        tags: Array.isArray(parsedFilters.tags) ? parsedFilters.tags : defaultFilters.tags,
        customDateRange: parsedFilters.customDateRange || defaultFilters.customDateRange
      };
      
      // Additional validation - ensure no undefined values
      Object.keys(loadedFilters).forEach(key => {
        if (loadedFilters[key as keyof TransactionStoreFilters] === undefined || loadedFilters[key as keyof TransactionStoreFilters] === null) {
          (loadedFilters as any)[key] = (defaultFilters as any)[key];
        }
      });
      
      debugLog('Processed loaded filters (no undefined values):', loadedFilters);
      return loadedFilters;
    }
  } catch (error) {
    console.error('Error loading saved filters:', error);
  }
  
  debugLog('No saved filters found, using defaults');
  return defaultFilters;
};

// Initialize filters with saved values
const initialFilters = loadSavedFilters();

export interface TransactionInput {
  account_id: string;
  to_account_id?: string;
  category_id?: string;
  payee_id?: string;
  payee_name?: string;
  type: 'income' | 'expense' | 'transfer';
  amount: number;
  currency: string;
  date: string;
  description?: string;
  notes?: string;
  status?: 'pending' | 'completed' | 'cancelled';
  exchange_rate?: number;
  converted_amount?: number;
  location?: Record<string, any>;
  is_recurring?: boolean;
  recurring_rule_id?: string;
  metadata?: Record<string, any>;
  tags?: string[];
}

export interface TransactionStore {
  transactions: Transaction[];
  categories: Category[];
  tags: string[];
  isLoading: boolean;
  error: string | null;
  filters: TransactionStoreFilters;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  filtersInitialized: boolean;
  pagination: {
    hasMore: boolean;
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Internal state
  _isRefreshingForFilters: boolean;
  _lastFetchKey: string;
  
  // Methods
  setTransactions: (transactions: Transaction[]) => void;
  deleteTransaction: (id: string) => Promise<void>;
  fetchTransactions: (userId: string, options?: { page?: number; pageSize?: number; cursor?: string; orderBy?: string; orderDirection?: 'asc' | 'desc' }) => Promise<Transaction[]>;
  fetchNextPage: (userId: string, options?: { limit?: number; filters?: any; cursor?: string }) => Promise<Transaction[]>;
  getFilteredTransactions: () => Transaction[];
  updateTransaction: (id: string, data: Partial<TransactionInput>) => Promise<boolean>;
  addTransaction: (data: TransactionInput) => Promise<string | null>;
  setFilters: (filters: Partial<TransactionStoreFilters>) => void;
  resetFilters: () => void;
  setSorting: (field: string, direction: 'asc' | 'desc') => void;
  getAccountBalance: (accountId: string) => number;
  clearCache: () => void;
  
  // Debug methods
  debug: {
    logStateInfo: () => void;
  }
}

const sortTransactions = (
  transactions: Transaction[],
  field: string,
  direction: 'asc' | 'desc'
) => {
  return [...transactions].sort((a, b) => {
    let aValue = a[field as keyof Transaction] ?? '';
    let bValue = b[field as keyof Transaction] ?? '';

    // Handle special cases
    if (field === 'date') {
      // Ensure we're working with Date objects or timestamps
      const aDate = aValue instanceof Date ? aValue : new Date(aValue as string);
      const bDate = bValue instanceof Date ? bValue : new Date(bValue as string);
      
      // Convert to timestamps for reliable comparison
      aValue = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
      bValue = isNaN(bDate.getTime()) ? 0 : bDate.getTime();
    } else if (field === 'payee_name') {
      aValue = (aValue as string) || '';
      bValue = (bValue as string) || '';
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

const filterTransactions = (transactions: Transaction[], filters: TransactionStoreFilters): Transaction[] => {
  // Debug the input transactions if in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('=== TRANSACTION FILTERING DEBUG ===');
    console.log('Filtering', transactions.length, 'transactions with filters:', filters);
    
    // Count transactions with no category/payee for debugging
    const noCategory = transactions.filter(t => !t.category_id).length;
    const noPayee = transactions.filter(t => !t.payee_id && !t.payee_name).length;
    console.log(`Before filtering: ${noCategory} transactions with no category, ${noPayee} with no payee`);
  }

  // Create a filtered copy of the transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Date range filter
    if (filters.dateRange && filters.dateRange !== 'all') {
      // Make sure we have a valid date string before parsing
      if (!transaction.date) {
        return false;
      }

      const transactionDate = parseISO(transaction.date);
      const now = new Date();
      
      let start: Date;
      let end: Date;
      
      switch (filters.dateRange) {
        case 'today':
          start = startOfDay(now);
          end = endOfDay(now);
          break;
        case 'yesterday': {
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          start = startOfDay(yesterday);
          end = endOfDay(yesterday);
          break;
        }
        case 'thisWeek':
          start = startOfWeek(now, { weekStartsOn: 1 }); // Week starts on Monday
          end = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'lastWeek': {
          const lastWeek = new Date(now);
          lastWeek.setDate(lastWeek.getDate() - 7);
          start = startOfWeek(lastWeek, { weekStartsOn: 1 });
          end = endOfWeek(lastWeek, { weekStartsOn: 1 });
          break;
        }
        case 'thisMonth':
          start = startOfMonth(now);
          end = endOfMonth(now);
          break;
        case 'lastMonth': {
          const lastMonth = new Date(now);
          lastMonth.setMonth(lastMonth.getMonth() - 1);
          start = startOfMonth(lastMonth);
          end = endOfMonth(lastMonth);
          break;
        }
        case 'thisQuarter':
          start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
          end = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
          break;
        case 'lastQuarter': {
          const lastQuarter = new Date(now);
          lastQuarter.setMonth(lastQuarter.getMonth() - 3);
          start = new Date(lastQuarter.getFullYear(), Math.floor(lastQuarter.getMonth() / 3) * 3, 1);
          end = new Date(lastQuarter.getFullYear(), Math.floor(lastQuarter.getMonth() / 3) * 3 + 3, 0);
          break;
        }
        case 'thisYear':
          start = startOfYear(now);
          end = endOfYear(now);
          break;
        case 'lastYear': {
          const lastYear = new Date(now);
          lastYear.setFullYear(lastYear.getFullYear() - 1);
          start = startOfYear(lastYear);
          end = endOfYear(lastYear);
          break;
        }
        case 'custom':
          if (filters.customDateRange?.from && filters.customDateRange?.to) {
            start = startOfDay(parseISO(filters.customDateRange.from));
            end = endOfDay(parseISO(filters.customDateRange.to));
          } else {
            // If custom date range is not properly set, don't filter
            return true;
          }
          break;
        default:
          return true;
      }
      
      try {
        if (!isWithinInterval(transactionDate, { start, end })) {
          return false;
        }
      } catch (error) {
        // Don't filter out if there's an error
        return true;
      }
    }

    // Type filter
    if (filters.type && filters.type !== 'all' && transaction.type !== filters.type) {
      return false;
    }

    // Category filter
    if (filters.category && filters.category !== 'all' && filters.category !== '' && filters.category != null) {
      // Special case for "none" - match transactions with no category
      if (filters.category === 'none') {
        // Get the category ID
        const categoryId = transaction.category_id;
        
        // A transaction has no category if category_id is empty/null/undefined
        // Important: We must only check for truthy/falsy values, not exact equality
        if (!categoryId) {
          return true;
        }
        return false;
      } else {
        // Normal category filter - check if category matches specific ID
        const transactionCategoryId = transaction.category_id;
        
        if (transactionCategoryId !== filters.category) {
          return false;
        }
      }
    }

    // Account filter
    if (filters.accounts && Array.isArray(filters.accounts) && filters.accounts.length > 0) {
      // Clean up filter accounts to remove any empty strings or null values
      const validFilterAccountIds = filters.accounts
        .filter(id => id !== null && id !== undefined && id !== '')
        .map(id => String(id));
      
      if (validFilterAccountIds.length > 0) {
        // Get the account ID from either account_id (core format) or accountId (web format)
        let transactionAccountId: string | undefined;
        
        // First check the core format property (account_id)
        if (typeof transaction.account_id !== 'undefined') {
          transactionAccountId = String(transaction.account_id);
        } 
        // Then check the web format property (accountId)
        else if (typeof (transaction as any).accountId !== 'undefined') {
          transactionAccountId = String((transaction as any).accountId);
        }
        
        if (!transactionAccountId) {
          // If the transaction has no account ID at all, it can't match the filter
          return false;
        }
        
        // Check if the transaction's account ID is in the filter list
        const isAccountMatch = validFilterAccountIds.includes(transactionAccountId);
        
        if (!isAccountMatch) {
          return false;
        }
      }
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const description = transaction.description?.toLowerCase() || '';
      const notes = transaction.notes?.toLowerCase() || '';
      const payeeName = (transaction.payee_name || '').toLowerCase();
      
      if (!description.includes(searchLower) && 
          !notes.includes(searchLower) && 
          !payeeName.includes(searchLower)) {
        return false;
      }
    }

    // Payee filter
    if (filters.payee && filters.payee !== 'all' && filters.payee !== '' && filters.payee != null) {
      // Special case for "none" - match transactions with no payee
      if (filters.payee === 'none') {
        // Get the payee information - check both formats just to be safe
        const payeeId = transaction.payee_id;
        const payeeName = transaction.payee_name;
        
        // A transaction has no payee if BOTH payee_id AND payee_name are empty
        // Important: We must only check for truthy/falsy values, not exact equality
        if (!payeeId && !payeeName) {
          return true;
        }
        return false;
      } else {
        // Normal payee filter - check if payee matches specific ID or name
        const payeeId = transaction.payee_id ? String(transaction.payee_id) : '';
        const payeeName = transaction.payee_name || '';
        const filterPayee = String(filters.payee);
        
        // Match if payee ID or name matches filter value
        const payeeMatch = payeeId === filterPayee || payeeName === filterPayee;
        
        if (!payeeMatch) {
          return false;
        }
      }
    }

    // Tags filter
    if (filters.tags && Array.isArray(filters.tags) && filters.tags.length > 0) {
      // Check if transaction has the required tags
      const transactionTags = transaction.tags || [];
      
      // We want to match if the transaction contains ANY of the filtered tags
      const tagsMatch = filters.tags.some(tag => transactionTags.includes(tag));
      
      if (!tagsMatch) {
        return false;
      }
    }

    return true;
  });
  
  // Debug the output if in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('After filtering:', filteredTransactions.length, 'transactions remain');
    
    // If filtering for no category or no payee, show detailed results
    if (filters.category === 'none' || filters.payee === 'none') {
      console.log('Detailed filtered results:');
      filteredTransactions.slice(0, 3).forEach(t => {
        console.log(`Transaction ${t.id}: category_id=${t.category_id}, payee_id=${t.payee_id}, payee_name=${t.payee_name}`);
      });
    }
    
    console.log('=== END FILTERING DEBUG ===');
  }
  
  return filteredTransactions;
};

export const useTransactionStore = create<TransactionStore>((set, get) => ({
  transactions: [],
  categories: [],
  tags: [],
  isLoading: false,
  error: null,
  filters: initialFilters, // Use initialFilters here
  sortField: 'date',
  sortDirection: 'desc',
  filtersInitialized: true, // Set to true as filters are loaded from localStorage
  pagination: {
    hasMore: false,
    page: 1,
    pageSize: 200,
    total: 0
  },
  
  // Internal flag to track filter-triggered refreshes
  _isRefreshingForFilters: false,
  _lastFetchKey: '',

  setTransactions: (transactions) => set({ transactions }),

  // Clear the localStorage cache for transactions
  clearCache: () => {
    // Clear the local state
    set({ transactions: [] });
    
    // Clear the core store's transactions
    useCoreTransactionStore.getState().clearTransactions();
    
    // Remove the transactions-storage item from localStorage
    localStorage.removeItem('transactions-storage');
    
    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    
    console.log('Transaction cache cleared');
  },

  deleteTransaction: async (id) => {
    try {
      debugLog('Deleting transaction', { id });
      
      // Call Supabase to delete the transaction
      const { data, error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Update local state
      set(state => ({
        transactions: state.transactions.filter(t => t.id !== id)
      }));
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      
      toast.success('Transaction deleted');
      
      return data;
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Failed to delete transaction');
      throw error;
    }
  },

  fetchTransactions: async (userId, options = {}) => {
    try {
      debugLog('Fetching transactions', { userId, options });
      
      // Handle case where userId might be undefined or empty
      if (!userId) {
        // Try to get userId from auth store as fallback
        const authUser = useAuthStore.getState().user;
        const authUserId = authUser?.id;
        
        if (!authUserId) {
          console.error('fetchTransactions: No userId provided and no user in authStore');
          return [];
        }
        
        userId = authUserId;
        debugLog('Using userId from authStore', { userId });
      }
      
      // Extract pagination options
      const { page = 1, pageSize = 200, orderBy = 'date', orderDirection = 'desc' } = options;
      
      // Get current filters to apply server-side filtering
      const currentFilters = get().filters;
      
      // Create a unique key for this fetch request to prevent duplicates
      const fetchKey = JSON.stringify({
        userId,
        page,
        pageSize,
        orderBy,
        orderDirection,
        filters: currentFilters
      });
      
      // Check if this exact fetch was already performed recently
      const state = get();
      if (state._lastFetchKey === fetchKey && state.isLoading) {
        debugLog('Skipping duplicate fetch request with same parameters');
        return state.transactions;
      }
      
      // Update the last fetch key and loading state
      set({ 
        isLoading: true, 
        error: null,
        _lastFetchKey: fetchKey
      });
      
      debugLog('Applying current filters to server query:', currentFilters);
      
      // Fetch transactions from Supabase with pagination
      debugLog('Executing Supabase query with pagination', { userId, page, pageSize });
      
      // Use the pagination utility function if available
      let query;
      try {
        // Use our local pagination utility functions
        // Build the query with pagination
        query = supabase
          .from('transactions')
          .select('*', { count: 'exact' })
          .eq('user_id', userId)
          .order(orderBy, { ascending: orderDirection === 'asc' })
          .range((page - 1) * pageSize, page * pageSize - 1);
        
        debugLog('Using query with pagination', { page, pageSize });
      } catch (error) {
        console.warn('Could not use query, falling back to basic query', error);
        
        // Fallback to basic query if the optimization module is not available
        query = supabase
          .from('transactions')
          .select('*')
          .eq('user_id', userId)
          .order(orderBy, { ascending: orderDirection === 'asc' })
          .range((page - 1) * pageSize, page * pageSize - 1);
      }
      
      // Apply account filter server-side if present
      if (currentFilters.accounts && Array.isArray(currentFilters.accounts) && currentFilters.accounts.length > 0) {
        const validAccountIds = currentFilters.accounts.filter(id => id && id !== '');
        if (validAccountIds.length > 0) {
          if (validAccountIds.length === 1) {
            // Single account - only show transactions where account_id matches (not to_account_id)
            // This ensures we only see the relevant side of transfer transactions
            query = query.eq('account_id', validAccountIds[0]);
          } else {
            // Multiple accounts - only show transactions where account_id is in the list
            query = query.in('account_id', validAccountIds);
          }
          debugLog('Applied account filter to server query (account_id only):', validAccountIds);
        }
      }
      
      // Apply type filter server-side if present
      if (currentFilters.type && currentFilters.type !== 'all') {
        query = query.eq('type', currentFilters.type);
        debugLog('Applied type filter to server query:', currentFilters.type);
      }
      
      // Apply category filter server-side if present
      if (currentFilters.category && currentFilters.category !== 'all') {
        if (currentFilters.category === 'none') {
          query = query.is('category_id', null);
        } else {
          query = query.eq('category_id', currentFilters.category);
        }
        debugLog('Applied category filter to server query:', currentFilters.category);
      }
      
            // Apply search filter server-side if present
      if (currentFilters.search && currentFilters.search.trim() !== '') {
        const searchTerm = currentFilters.search.trim();
        
        // Use quoted values to handle commas and special characters properly in PostgREST
        const searchPattern = `%${searchTerm}%`;
        query = query.or(`description.ilike."${searchPattern}",notes.ilike."${searchPattern}",payee_name.ilike."${searchPattern}"`);
        debugLog('Applied search filter to server query');
      }
      
      // Apply payee filter server-side if present
      if (currentFilters.payee && currentFilters.payee !== 'all') {
        if (currentFilters.payee === 'none') {
          // Match transactions with no payee (both payee_id and payee_name are null/empty)
          query = query.or('payee_id.is.null,payee_name.is.null');
        } else {
          query = query.or(`payee_id.eq.${currentFilters.payee},payee_name.eq.${currentFilters.payee}`);
        }
        debugLog('Applied payee filter to server query:', currentFilters.payee);
      }
      
      // Apply tags filter server-side if present
      if (currentFilters.tags && Array.isArray(currentFilters.tags) && currentFilters.tags.length > 0) {
        // Use Supabase array overlap operator to check if transaction tags contain any of the filtered tags
        const tagsArray = currentFilters.tags.map(tag => `"${tag}"`).join(',');
        query = query.overlaps('tags', currentFilters.tags);
        debugLog('Applied tags filter to server query:', currentFilters.tags);
      }

      // Apply date filter server-side if present
      if (currentFilters.dateRange && currentFilters.dateRange !== 'all') {
        const now = new Date();
        let startDate: Date | null = null;
        let endDate: Date | null = null;

        switch (currentFilters.dateRange) {
          case 'today':
            startDate = startOfDay(now);
            endDate = endOfDay(now);
            break;
          case 'yesterday': {
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            startDate = startOfDay(yesterday);
            endDate = endOfDay(yesterday);
            break;
          }
          case 'thisWeek':
            startDate = startOfWeek(now, { weekStartsOn: 1 });
            endDate = endOfWeek(now, { weekStartsOn: 1 });
            break;
          case 'lastWeek': {
            const lastWeek = new Date(now);
            lastWeek.setDate(lastWeek.getDate() - 7);
            startDate = startOfWeek(lastWeek, { weekStartsOn: 1 });
            endDate = endOfWeek(lastWeek, { weekStartsOn: 1 });
            break;
          }
          case 'thisMonth':
            startDate = startOfMonth(now);
            endDate = endOfMonth(now);
            break;
          case 'lastMonth': {
            const lastMonth = new Date(now);
            lastMonth.setMonth(lastMonth.getMonth() - 1);
            startDate = startOfMonth(lastMonth);
            endDate = endOfMonth(lastMonth);
            break;
          }
          case 'thisQuarter':
            startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
            endDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
            break;
          case 'lastQuarter': {
            const lastQuarter = new Date(now);
            lastQuarter.setMonth(lastQuarter.getMonth() - 3);
            startDate = new Date(lastQuarter.getFullYear(), Math.floor(lastQuarter.getMonth() / 3) * 3, 1);
            endDate = new Date(lastQuarter.getFullYear(), Math.floor(lastQuarter.getMonth() / 3) * 3 + 3, 0);
            break;
          }
          case 'thisYear':
            startDate = startOfYear(now);
            endDate = endOfYear(now);
            break;
          case 'lastYear': {
            const lastYear = new Date(now);
            lastYear.setFullYear(lastYear.getFullYear() - 1);
            startDate = startOfYear(lastYear);
            endDate = endOfYear(lastYear);
            break;
          }
          case 'custom':
            if (currentFilters.customDateRange?.from && currentFilters.customDateRange?.to) {
              startDate = startOfDay(parseISO(currentFilters.customDateRange.from));
              endDate = endOfDay(parseISO(currentFilters.customDateRange.to));
            }
            break;
        }

        if (startDate && endDate) {
          query = query.gte('date', startDate.toISOString()).lte('date', endDate.toISOString());
          debugLog('Applied date filter to server query:', { 
            dateRange: currentFilters.dateRange, 
            startDate: startDate.toISOString(), 
            endDate: endDate.toISOString() 
          });
        }
      }
      
      const { data: transactions, error, count } = await query;
      
      if (error) {
        console.error('Supabase error fetching transactions:', error);
        throw error;
      }
      
      const validTransactions = Array.isArray(transactions) ? transactions : [];
      
      debugLog(`Fetched ${validTransactions.length} transactions with server-side filtering, total count: ${count}`);
      
      // Update state with fetched transactions
      set({
        transactions: validTransactions,
        isLoading: false,
        filtersInitialized: true,
        pagination: {
          hasMore: (count || 0) > validTransactions.length,
          page,
          pageSize,
          total: count || 0
        }
      });
      
      return validTransactions;
    } catch (error) {
      console.error('Error in fetchTransactions:', error);
      
      // Update error state
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch transactions',
        isLoading: false,
        filtersInitialized: true
      });
      
      // Show toast error to user
      toast.error('Failed to load transactions');
      
      return [];
    }
  },

  fetchNextPage: async (userId, options = {}) => {
    try {
      const { limit = 200, cursor } = options;
      const state = get();
      
      // Don't fetch if we're already loading or there's no more data
      if (state.isLoading || !state.pagination.hasMore) {
        debugLog('Skipping fetchNextPage:', { 
          isLoading: state.isLoading, 
          hasMore: state.pagination.hasMore 
        });
        return [];
      }
      
      debugLog('Fetching next page of transactions', { 
        userId, 
        cursor, 
        limit, 
        currentPage: state.pagination.page,
        currentTransactionCount: state.transactions?.length || 0 
      });
      
      // Update loading state
      set({ isLoading: true });
      
      // Get current filters to apply server-side filtering
      const currentFilters = state.filters;
      debugLog('Applying current filters to next page query:', currentFilters);
      
      // Calculate proper offset based on actual transaction count (not page number)
      // This ensures we always fetch from the correct position
      const currentTransactionCount = state.transactions?.length || 0;
      const offset = currentTransactionCount;
      
      // Create the query with proper offset pagination instead of cursor
      let query = supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .range(offset, offset + limit - 1); // Use range for proper pagination
      
      debugLog('Using offset pagination:', { offset, limit, range: `${offset}-${offset + limit - 1}` });
      
      // Apply account filter server-side if present
      if (currentFilters.accounts && Array.isArray(currentFilters.accounts) && currentFilters.accounts.length > 0) {
        const validAccountIds = currentFilters.accounts.filter(id => id && id !== '');
        if (validAccountIds.length > 0) {
          if (validAccountIds.length === 1) {
            // Single account - use OR condition for account_id or to_account_id (for transfers)
            query = query.or(`account_id.eq.${validAccountIds[0]},to_account_id.eq.${validAccountIds[0]}`);
          } else {
            // Multiple accounts - use IN condition
            query = query.or(`account_id.in.(${validAccountIds.join(',')}),to_account_id.in.(${validAccountIds.join(',')})`);
          }
          debugLog('Applied account filter to next page query:', validAccountIds);
        }
      }
      
      // Apply type filter server-side if present
      if (currentFilters.type && currentFilters.type !== 'all') {
        query = query.eq('type', currentFilters.type);
        debugLog('Applied type filter to next page query:', currentFilters.type);
      }
      
      // Apply category filter server-side if present
      if (currentFilters.category && currentFilters.category !== 'all') {
        if (currentFilters.category === 'none') {
          query = query.is('category_id', null);
        } else {
          query = query.eq('category_id', currentFilters.category);
        }
        debugLog('Applied category filter to next page query:', currentFilters.category);
      }
      
      // Apply search filter server-side if present
      if (currentFilters.search && currentFilters.search.trim() !== '') {
        const searchTerm = currentFilters.search.trim();
        
        // Use quoted values to handle commas and special characters properly in PostgREST
        const searchPattern = `%${searchTerm}%`;
        query = query.or(`description.ilike."${searchPattern}",notes.ilike."${searchPattern}",payee_name.ilike."${searchPattern}"`);
        debugLog('Applied search filter to next page query');
      }
      
             // Apply payee filter server-side if present
       if (currentFilters.payee && currentFilters.payee !== 'all') {
         if (currentFilters.payee === 'none') {
           // Match transactions with no payee (both payee_id and payee_name are null/empty)
           query = query.or('payee_id.is.null,payee_name.is.null');
         } else {
           query = query.or(`payee_id.eq.${currentFilters.payee},payee_name.eq.${currentFilters.payee}`);
         }
         debugLog('Applied payee filter to next page query:', currentFilters.payee);
       }
      
      // Apply tags filter server-side if present
      if (currentFilters.tags && Array.isArray(currentFilters.tags) && currentFilters.tags.length > 0) {
        // Use Supabase array overlap operator to check if transaction tags contain any of the filtered tags
        const tagsArray = currentFilters.tags.map(tag => `"${tag}"`).join(',');
        query = query.overlaps('tags', currentFilters.tags);
        debugLog('Applied tags filter to next page query:', currentFilters.tags);
      }

      // Apply date filter server-side if present
      if (currentFilters.dateRange && currentFilters.dateRange !== 'all') {
        const now = new Date();
        let startDate: Date | null = null;
        let endDate: Date | null = null;

        switch (currentFilters.dateRange) {
          case 'today':
            startDate = startOfDay(now);
            endDate = endOfDay(now);
            break;
          case 'yesterday': {
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            startDate = startOfDay(yesterday);
            endDate = endOfDay(yesterday);
            break;
          }
          case 'thisWeek':
            startDate = startOfWeek(now, { weekStartsOn: 1 });
            endDate = endOfWeek(now, { weekStartsOn: 1 });
            break;
          case 'lastWeek': {
            const lastWeek = new Date(now);
            lastWeek.setDate(lastWeek.getDate() - 7);
            startDate = startOfWeek(lastWeek, { weekStartsOn: 1 });
            endDate = endOfWeek(lastWeek, { weekStartsOn: 1 });
            break;
          }
          case 'thisMonth':
            startDate = startOfMonth(now);
            endDate = endOfMonth(now);
            break;
          case 'lastMonth': {
            const lastMonth = new Date(now);
            lastMonth.setMonth(lastMonth.getMonth() - 1);
            startDate = startOfMonth(lastMonth);
            endDate = endOfMonth(lastMonth);
            break;
          }
          case 'thisQuarter':
            startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
            endDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
            break;
          case 'lastQuarter': {
            const lastQuarter = new Date(now);
            lastQuarter.setMonth(lastQuarter.getMonth() - 3);
            startDate = new Date(lastQuarter.getFullYear(), Math.floor(lastQuarter.getMonth() / 3) * 3, 1);
            endDate = new Date(lastQuarter.getFullYear(), Math.floor(lastQuarter.getMonth() / 3) * 3 + 3, 0);
            break;
          }
          case 'thisYear':
            startDate = startOfYear(now);
            endDate = endOfYear(now);
            break;
          case 'lastYear': {
            const lastYear = new Date(now);
            lastYear.setFullYear(lastYear.getFullYear() - 1);
            startDate = startOfYear(lastYear);
            endDate = endOfYear(lastYear);
            break;
          }
          case 'custom':
            if (currentFilters.customDateRange?.from && currentFilters.customDateRange?.to) {
              startDate = startOfDay(parseISO(currentFilters.customDateRange.from));
              endDate = endOfDay(parseISO(currentFilters.customDateRange.to));
            }
            break;
        }

        if (startDate && endDate) {
          query = query.gte('date', startDate.toISOString()).lte('date', endDate.toISOString());
          debugLog('Applied date filter to next page query:', { 
            dateRange: currentFilters.dateRange, 
            startDate: startDate.toISOString(), 
            endDate: endDate.toISOString() 
          });
        }
      }
      
      // Execute the query
      const { data: newTransactions, error, count } = await query;
      
      if (error) {
        throw error;
      }
      
            const validTransactions = Array.isArray(newTransactions) ? newTransactions : [];
      debugLog(`Fetched ${validTransactions.length} more transactions with filtering, total available: ${count}`);
      
      // Get the current transaction list and append new transactions
      const currentTransactions = state.transactions || [];
      const newTotalTransactions = [...currentTransactions, ...validTransactions];
      const hasMore = newTotalTransactions.length < (count || 0);
      
      debugLog('Successfully appended transactions:', {
        previousCount: currentTransactions.length,
        newCount: validTransactions.length,
        totalCount: newTotalTransactions.length,
        hasMore,
        totalAvailable: count
      });
      
      // Update state with appended transactions
      set({
        transactions: newTotalTransactions,
        isLoading: false,
        pagination: {
          hasMore,
          page: Math.ceil(newTotalTransactions.length / limit),
          pageSize: limit,
          total: count || 0
        }
      });
      
      return validTransactions;
    } catch (error) {
      console.error('Error in fetchNextPage:', error);
      set({ isLoading: false });
      toast.error('Failed to load more transactions');
      return [];
    }
  },

  getFilteredTransactions: () => {
    debugLog("getFilteredTransactions called");
    
    const { transactions, filters, sortField, sortDirection, filtersInitialized } = get();
    
    // If filters aren't initialized yet, return all transactions
    if (!filtersInitialized) {
      debugLog("Filters not initialized yet, returning all transactions");
      return sortTransactions(transactions, sortField, sortDirection);
    }
    
    // Since major filters (account, type, category, search) are now applied server-side,
    // we only need to apply remaining client-side filters that couldn't be done server-side
    const filtered = filterTransactions(transactions, filters);
    return sortTransactions(filtered, sortField, sortDirection);
  },

  updateTransaction: async (id, data) => {
    const userId = useAuthStore.getState().user?.id;
    
    if (!userId) {
      toast.error('You must be logged in to update a transaction');
      return false;
    }

    try {
      // Convert camelCase field names to snake_case for Supabase compatibility
      const convertToSnakeCase = (obj: any): any => {
        const converted: any = {};
        
        // Handle specific field mappings
        if (obj.accountId !== undefined) converted.account_id = obj.accountId;
        if (obj.categoryId !== undefined) converted.category_id = obj.categoryId;
        if (obj.payeeId !== undefined) converted.payee_id = obj.payeeId;
        if (obj.payeeName !== undefined) converted.payee_name = obj.payeeName;
        if (obj.toAccountId !== undefined) converted.to_account_id = obj.toAccountId;
        if (obj.exchangeRate !== undefined) converted.exchange_rate = obj.exchangeRate;
        if (obj.userId !== undefined) converted.user_id = obj.userId;
        
        // Handle direct snake_case fields (already correct)
        if (obj.account_id !== undefined) converted.account_id = obj.account_id;
        if (obj.category_id !== undefined) converted.category_id = obj.category_id;
        if (obj.payee_id !== undefined) converted.payee_id = obj.payee_id;
        if (obj.payee_name !== undefined) converted.payee_name = obj.payee_name;
        if (obj.to_account_id !== undefined) converted.to_account_id = obj.to_account_id;
        if (obj.exchange_rate !== undefined) converted.exchange_rate = obj.exchange_rate;
        if (obj.user_id !== undefined) converted.user_id = obj.user_id;
        
        // Handle fields that don't need conversion
        if (obj.id !== undefined) converted.id = obj.id;
        if (obj.type !== undefined) converted.type = obj.type;
        if (obj.amount !== undefined) converted.amount = obj.amount;
        if (obj.date !== undefined) converted.date = obj.date;
        if (obj.description !== undefined) converted.description = obj.description;
        if (obj.notes !== undefined) converted.notes = obj.notes;
        if (obj.status !== undefined) converted.status = obj.status;
        if (obj.tags !== undefined) converted.tags = obj.tags;
        if (obj.metadata !== undefined) converted.metadata = obj.metadata;
        if (obj.created_at !== undefined) converted.created_at = obj.created_at;
        if (obj.updated_at !== undefined) converted.updated_at = obj.updated_at;
        
        return converted;
      };

      const compatibleData = convertToSnakeCase({
        ...data,
        status: data.status as 'pending' | 'completed' | 'cancelled' | undefined
      });
      
      debugLog('Converting transaction data for core store:', { original: data, converted: compatibleData });
      
      await useCoreTransactionStore.getState().updateTransaction(userId, id, compatibleData);
      
      // Update local state with proper field mapping
      set((state) => {
        const updatedTransactions = state.transactions.map(t => {
          if (t.id === id) {
            // Create updated transaction with proper field mapping
            const updatedTransaction = { ...t };
            
            // Map fields from data (already converted to snake_case by convertToSnakeCase function)
            if ((data as any).accountId !== undefined) updatedTransaction.account_id = (data as any).accountId;
            if ((data as any).categoryId !== undefined) updatedTransaction.category_id = (data as any).categoryId;
            if ((data as any).payeeId !== undefined) updatedTransaction.payee_id = (data as any).payeeId;
            if ((data as any).payeeName !== undefined) updatedTransaction.payee_name = (data as any).payeeName;
            if ((data as any).toAccountId !== undefined) updatedTransaction.to_account_id = (data as any).toAccountId;
            if ((data as any).exchangeRate !== undefined) updatedTransaction.exchange_rate = (data as any).exchangeRate;
            
            // Also handle snake_case fields that may be in data
            if (data.account_id !== undefined) updatedTransaction.account_id = data.account_id;
            if (data.category_id !== undefined) updatedTransaction.category_id = data.category_id;
            if (data.payee_id !== undefined) updatedTransaction.payee_id = data.payee_id;
            if (data.payee_name !== undefined) updatedTransaction.payee_name = data.payee_name;
            if (data.to_account_id !== undefined) updatedTransaction.to_account_id = data.to_account_id;
            if (data.exchange_rate !== undefined) updatedTransaction.exchange_rate = data.exchange_rate;
            
            // Direct field updates
            if (data.type !== undefined) updatedTransaction.type = data.type;
            if (data.amount !== undefined) updatedTransaction.amount = data.amount;
            if (data.date !== undefined) updatedTransaction.date = data.date;
            if (data.description !== undefined) updatedTransaction.description = data.description;
            if (data.notes !== undefined) updatedTransaction.notes = data.notes;
            if (data.status !== undefined) updatedTransaction.status = data.status;
            if (data.tags !== undefined) updatedTransaction.tags = data.tags;
            if (data.metadata !== undefined) updatedTransaction.metadata = data.metadata;
            
            // Update timestamp
            updatedTransaction.updated_at = new Date().toISOString();
            
            return updatedTransaction as Transaction;
          }
          return t;
        });
        return { transactions: updatedTransactions };
      });

      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      
      // Force refresh the filtered transactions by clearing cache
      debugLog('Transaction updated successfully, forcing cache refresh');

      toast.success('Transaction updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update transaction');
      return false;
    }
  },

  addTransaction: async (data) => {
    const userId = useAuthStore.getState().user?.id;
    
    if (!userId) {
      toast.error('You must be logged in to add a transaction');
      return null;
    }

    try {
      const now = new Date().toISOString();
      const transactionId = uuidv4(); // Generate a new ID
      
      // Ensure data has compatible status values
      const transactionData = {
        ...data,
        id: transactionId, // Add the generated ID
        date: data.date || now,
        status: data.status || 'completed',
        created_at: now,
        updated_at: now,
        user_id: userId
      };

      // Convert to compatible format for core store
      const coreTransactionData = {
        ...transactionData,
        status: transactionData.status as 'pending' | 'completed' | 'cancelled'
      };

      // Call core store method which returns void
      await useCoreTransactionStore.getState().addTransaction(userId, coreTransactionData);
      
      // Update local state with type assertion to ensure compatibility
      set((state) => {
        const newTransaction = {
          ...transactionData,
          id: transactionId
        } as Transaction;
        
        return { 
          transactions: [newTransaction, ...state.transactions]
        };
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });

      toast.success('Transaction added successfully');
      return transactionId;
    } catch (error) {
      console.error('Error adding transaction:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add transaction');
      return null;
    }
  },

  setFilters: (filters) => {
    // Get existing filters
    const existingFilters = get().filters;
    
    // Create new filters, ensuring proper type conversion
    const newFilters = {
      ...existingFilters,
      ...filters
    };
    
    // Special handling for category filter
    if ('category' in filters) {
      if (filters.category === null || filters.category === undefined || filters.category === '') {
        newFilters.category = 'all';
      }
    }
    
    // Special handling for payee filter
    if ('payee' in filters) {
      if (filters.payee === null || filters.payee === undefined || filters.payee === '') {
        newFilters.payee = 'all';
      }
    }
    
    // Special handling for dateRange
    if ('dateRange' in filters) {
      // If switching to custom date range but no customDateRange data exists, initialize it
      if (filters.dateRange === 'custom' && !newFilters.customDateRange) {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        
        newFilters.customDateRange = {
          from: firstOfMonth.toISOString().split('T')[0],
          to: now.toISOString().split('T')[0]
        };
      }
    }
    
    // Special handling for accounts to ensure it's always valid
    if ('accounts' in filters) {
      if (Array.isArray(filters.accounts)) {
        // Ensure all account IDs are strings and filter out any nullish values
        const validAccounts = filters.accounts.filter(id => id !== null && id !== undefined && id !== '');
        
        // Convert all valid account IDs to strings
        newFilters.accounts = validAccounts.map(id => String(id));
        
        // If we're setting accounts to an empty array but the original array had values
        // that were just invalid (empty strings, null, etc.), preserve existing account filters
        if (newFilters.accounts.length === 0 && Array.isArray(filters.accounts) && filters.accounts.length > 0) {
          newFilters.accounts = Array.isArray(existingFilters.accounts) ? existingFilters.accounts : [];
        }
      } else if (filters.accounts === null || filters.accounts === undefined) {
        // If accounts is null/undefined, preserve existing account filters
        newFilters.accounts = Array.isArray(existingFilters.accounts) ? existingFilters.accounts : [];
      } else {
        // Convert single value to array if needed
        const accountId = String(filters.accounts);
        newFilters.accounts = [accountId];
      }
    }
    
    // Special handling for tags filter
    if ('tags' in filters) {
      if (!Array.isArray(filters.tags)) {
        newFilters.tags = [];
      }
    }
    
    // Check if server-side filterable properties have changed
    const accountFilterChanged = 
      'accounts' in filters && 
      JSON.stringify(existingFilters.accounts) !== JSON.stringify(newFilters.accounts);
    
    const typeFilterChanged = 
      'type' in filters && existingFilters.type !== newFilters.type;
    
    const categoryFilterChanged = 
      'category' in filters && existingFilters.category !== newFilters.category;
    
    const searchFilterChanged = 
      'search' in filters && existingFilters.search !== newFilters.search;
    
    const payeeFilterChanged = 
      'payee' in filters && existingFilters.payee !== newFilters.payee;
    
    const tagsFilterChanged = 
      'tags' in filters && JSON.stringify(existingFilters.tags) !== JSON.stringify(newFilters.tags);
    
    const dateFilterChanged = 
      ('dateRange' in filters && existingFilters.dateRange !== newFilters.dateRange) ||
      ('customDateRange' in filters && JSON.stringify(existingFilters.customDateRange) !== JSON.stringify(newFilters.customDateRange));
    
    const shouldRefetch = accountFilterChanged || typeFilterChanged || categoryFilterChanged || searchFilterChanged || payeeFilterChanged || tagsFilterChanged || dateFilterChanged;
    
    if (shouldRefetch) {
      debugLog('Server-side filterable properties changed, will trigger re-fetch:', {
        accountFilterChanged,
        typeFilterChanged,
        categoryFilterChanged,
        searchFilterChanged,
        payeeFilterChanged,
        tagsFilterChanged,
        dateFilterChanged
      });
    }
    
    set({ 
      filters: newFilters,
      filtersInitialized: true 
    });
    
    // Save filters to localStorage
    try {
      localStorage.setItem('transaction-filters', JSON.stringify(newFilters));
    } catch (error) {
      console.error("Error saving filters to localStorage:", error);
    }
    
    // Trigger re-fetch if server-side filters changed (but avoid during initial load)
    // Use filtersInitialized instead of transaction count to determine if we're past initial load
    if (shouldRefetch && !get()._isRefreshingForFilters && get().filtersInitialized) {
      debugLog('Triggering re-fetch due to server-side filter changes');
      
      // Get current user to trigger refresh
      const authUser = useAuthStore.getState().user;
      if (authUser?.id) {
        // Set the flag to prevent infinite loops
        set({ _isRefreshingForFilters: true });
        
        // Use a timeout to ensure the filter state is updated before fetching
        setTimeout(() => {
          get().fetchTransactions(authUser.id, {
            page: 1,
            pageSize: 200,
            orderBy: 'date',
            orderDirection: 'desc'
          }).catch(error => {
            console.error('Error re-fetching transactions after filter change:', error);
          }).finally(() => {
            // Reset the flag after the fetch completes
            set({ _isRefreshingForFilters: false });
          });
        }, 200);
      }
    } else if (shouldRefetch && get().transactions.length === 0) {
      debugLog('Skipping re-fetch during initial load to avoid redundant calls');
    }
  },

  resetFilters: () => {
    set({ filters: defaultFilters, filtersInitialized: true });
    // Clear saved filters
    localStorage.removeItem('transaction-filters');
    debugLog("Filters reset to defaults");
  },

  setSorting: (field, direction) => {
    set({ sortField: field, sortDirection: direction });
  },

  getAccountBalance: (accountId) => {
    const { transactions } = get();
    return transactions
      .filter(t => t.account_id === accountId)
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  },

  // Add debug utility methods
  debug: {
    logStateInfo: () => {
      const state = get();
      debugLog("Current state:", {
        transactionsCount: state.transactions.length,
        filters: state.filters,
        sortField: state.sortField,
        sortDirection: state.sortDirection
      });
    }
  }
}));

export const createTransferTransaction = async (
  fromAccountId: string,
  toAccountId: string,
  amount: number,
  date: Date | string,
  description: string = '',
  tags: string[] = [],
  notes: string = '',
  currency: string = 'USD'
): Promise<{ sourceId: string | null, destinationId: string | null }> => {
  const userId = useAuthStore.getState().user?.id;
  
  if (!userId) {
    toast.error('You must be logged in to create a transfer');
    return { sourceId: null, destinationId: null };
  }

  try {
    const dateString = typeof date === 'string' ? date : date.toISOString();
    
    const sourceTransaction: TransactionInput = {
      account_id: fromAccountId,
      to_account_id: toAccountId,
      type: 'transfer',
      amount: -amount,
      currency,
      date: dateString,
      description,
      notes,
      tags,
      status: 'completed'
    };

    const destinationTransaction: TransactionInput = {
      account_id: toAccountId,
      type: 'transfer',
      amount,
      currency,
      date: dateString,
      description,
      notes,
      tags,
      status: 'completed'
    };

    // Use the store's addTransaction method which now returns string | null
    const sourceId = await useTransactionStore.getState().addTransaction(sourceTransaction);
    
    // Only create destination transaction if source was successful
    let destinationId: string | null = null;
    if (sourceId !== null) {
      destinationId = await useTransactionStore.getState().addTransaction(destinationTransaction);
    }

    if (sourceId !== null && destinationId !== null) {
      toast.success('Transfer created successfully');
    }

    return { sourceId, destinationId };
  } catch (error) {
    console.error('Error creating transfer:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to create transfer');
    return { sourceId: null, destinationId: null };
  }
}; 